import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["sx", "value", "inputProps", "InputProps", "mask", "rounded", "placeholderEmail", "wrapLabel", "helperText", "errorMessage", "onChange"];
import PropTypes from 'prop-types';
import * as React from 'react';
import { InputAdornment, useThemeProps } from '@mui/material';
import MuiTextField from '@mui/material/TextField';
import FormHelperText from '../FormHelperText';
import { extendSX } from '../helpers';
import { IconCalendarOutlined, IconEmailOutlined, IconPhoneOutlined, IconPlaceOutlined } from '../icons';
import { MaskedInput2FAcode, MaskedInputCurrency, MaskedInputDate, MaskedInputNumber, MaskedInputPhone, MaskedInputSSN, MaskedInputZipcode } from './components';

/**
 *
 * Demos:
 *
 * - [TextField](https://ui-packages.isbuildingluma.com/ui-core/react-text-field/)
 *
 * API:
 *
 * - [TextField API](https://ui-packages.isbuildingluma.com/ui-core/api/text-field/)
 * - inherits [MuiTextField API](https://mui.com/material-ui/api/text-field/)
 */
import { jsx as _jsx } from "react/jsx-runtime";
const TextField = /*#__PURE__*/React.forwardRef(function TextField(inProps, ref) {
  const props = useThemeProps({
    props: inProps,
    name: 'LumaTextField'
  });
  const {
      sx,
      value: valueProp,
      inputProps: inputPropsProp,
      InputProps,
      mask,
      rounded,
      placeholderEmail,
      wrapLabel,
      helperText,
      errorMessage,
      onChange
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const type = React.useMemo(() => {
    switch (mask) {
      case 'email':
        return 'email';
      case 'phone':
        return 'tel';
      default:
        return rest.type;
    }
  }, [mask, rest.type]);
  const placeholder = React.useMemo(() => {
    switch (mask) {
      case 'email':
        return placeholderEmail;
      default:
        return rest.placeholder;
    }
  }, [mask, placeholderEmail, rest.placeholder]);
  const valueProps = React.useMemo(() => {
    if (!valueProp && InputProps != null && InputProps.readOnly) {
      return {
        value: '—',
        inputProps: _extends({}, inputPropsProp, {
          value: '—'
        })
      };
    }
    return {
      value: valueProp,
      inputProps: inputPropsProp
    };
  }, [valueProp, InputProps == null ? void 0 : InputProps.readOnly, inputPropsProp]);
  const {
    MaskedInput: inputComponent,
    Icon: IconAdornment
  } = React.useMemo(() => {
    let MaskedInput;
    let Icon;
    switch (mask) {
      case '2facode':
        MaskedInput = MaskedInput2FAcode;
        break;
      case 'address':
        Icon = IconPlaceOutlined;
        break;
      case 'date':
        MaskedInput = MaskedInputDate;
        Icon = IconCalendarOutlined;
        break;
      case 'email':
        Icon = IconEmailOutlined;
        break;
      case 'currency':
        MaskedInput = MaskedInputCurrency;
        break;
      case 'number':
        MaskedInput = MaskedInputNumber;
        break;
      case 'phone':
        MaskedInput = MaskedInputPhone;
        Icon = IconPhoneOutlined;
        break;
      case 'ssn':
        MaskedInput = MaskedInputSSN;
        break;
      case 'zipcode':
        MaskedInput = MaskedInputZipcode;
        break;
      default:
        break;
    }
    if (InputProps != null && InputProps.readOnly) {
      MaskedInput = valueProp ? MaskedInput : undefined;
      Icon = undefined;
    }
    return {
      MaskedInput,
      Icon
    };
  }, [mask, InputProps == null ? void 0 : InputProps.readOnly, valueProp]);
  return /*#__PURE__*/_jsx(MuiTextField, _extends({
    sx: extendSX(sx, _extends({}, (InputProps == null ? void 0 : InputProps.readOnly) && {
      '& .MuiInputBase-input, & .MuiOutlinedInput-root': {
        pl: '0px !important'
      },
      '& .MuiOutlinedInput-notchedOutline': {
        border: 'none'
      }
    }, (InputProps == null ? void 0 : InputProps.readOnly) && rest.select && {
      '& .MuiSelect-select': {
        cursor: 'default'
      },
      '& .MuiSelect-icon': {
        display: 'none'
      }
    }, rounded && {
      '& .MuiInputBase-root': {
        borderRadius: 64
      }
    }, {
      '& .MuiFormLabel-root': _extends({}, wrapLabel && {
        whiteSpace: 'normal',
        height: 'auto'
      }),
      // Remove margin from the FormHelperText wrapper
      '& > .MuiFormHelperText-root': {
        margin: 0
      }
    })),
    ref: ref,
    type: type,
    placeholder: placeholder,
    InputProps: _extends({
      inputComponent
    }, InputProps, {
      startAdornment: (InputProps == null ? void 0 : InputProps.startAdornment) || IconAdornment && /*#__PURE__*/_jsx(InputAdornment, {
        position: "start",
        children: /*#__PURE__*/_jsx(IconAdornment, {})
      })
    }),
    onChange: onChange ? event => {
      let newValue = event.target.value;
      if (mask === 'number' || mask === 'currency') {
        newValue = parseFloat(newValue.replace(/[$,]/g, ''));
      } else if (mask) {
        newValue = newValue.replace(/[() _-]/g, '');
      }
      onChange(event, newValue);
    } : undefined
    // @ts-expect-error
    ,
    FormHelperTextProps: {
      component: 'div'
    },
    helperText: !helperText && !errorMessage ? undefined : /*#__PURE__*/_jsx(FormHelperText, {
      errorMessage: rest.error && !rest.disabled ? errorMessage : undefined,
      children: helperText
    })
  }, valueProps, rest));
});
process.env.NODE_ENV !== "production" ? TextField.propTypes /* remove-proptypes */ = {
  // ----------------------------- Warning --------------------------------
  // | These PropTypes are generated from the TypeScript type definitions |
  // | To update them edit "TextFieldProps.ts" and run "yarn proptypes"
  // ----------------------------------------------------------------------
  /**
   * @ignore
   */
  children: PropTypes.node,
  /**
   * If `true`, the component is disabled.
   * @default false
   */
  disabled: PropTypes.bool,
  /**
   * If `true`, the label is displayed in an error state.
   * @default false
   */
  error: PropTypes.bool,
  /**
   * Error message to be appended to the helperText
   */
  errorMessage: PropTypes.string,
  /**
   * The helper text content.
   */
  helperText: PropTypes.node,
  /**
   * [Attributes](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Attributes) applied to the `input` element.
   */
  inputProps: PropTypes.object,
  /**
   * Props applied to the Input element.
   * It will be a [`FilledInput`](/material-ui/api/filled-input/),
   * [`OutlinedInput`](/material-ui/api/outlined-input/) or [`Input`](/material-ui/api/input/)
   * component depending on the `variant` prop value.
   */
  InputProps: PropTypes.object,
  /**
   * If `dense` or `normal`, will adjust vertical spacing of this and contained components.
   * @default 'none'
   */
  margin: PropTypes.oneOf(['dense', 'none', 'normal']),
  /**
   * The mask to apply
   */
  mask: PropTypes.oneOf(['2facode', 'address', 'currency', 'date', 'email', 'number', 'phone', 'ssn', 'zipcode']),
  /**
   * Callback fired when the value is changed.
   *
   * @param {object} event The event source of the callback.
   * @param {string | number} parsedValue The value changed. This value will be parsed depending on the mask applied.
   * Eg `mask="currency"` will parse the value from "$1,002.95" > 1002.95
   */
  onChange: PropTypes.func,
  /**
   * The short hint displayed in the `input` before the user enters a value.
   */
  placeholder: PropTypes.string,
  /**
   * Placeholder for email fields
   */
  placeholderEmail: PropTypes.string,
  /**
   * To have rounded borders
   */
  rounded: PropTypes.bool,
  /**
   * Render a [`Select`](/material-ui/api/select/) element while passing the Input element to `Select` as `input` parameter.
   * If this option is set you must pass the options of the select as children.
   * @default false
   */
  select: PropTypes.bool,
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object, PropTypes.bool])), PropTypes.func, PropTypes.object]),
  /**
   * Type of the `input` element. It should be [a valid HTML5 input type](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#Form_%3Cinput%3E_types).
   */
  type: PropTypes.oneOfType([PropTypes.oneOf(['button', 'checkbox', 'color', 'date', 'datetime-local', 'email', 'file', 'hidden', 'image', 'month', 'number', 'password', 'radio', 'range', 'reset', 'search', 'submit', 'tel', 'text', 'time', 'url', 'week']), PropTypes.shape({
    '__@iterator@457': PropTypes.func.isRequired,
    anchor: PropTypes.func.isRequired,
    at: PropTypes.func.isRequired,
    big: PropTypes.func.isRequired,
    blink: PropTypes.func.isRequired,
    bold: PropTypes.func.isRequired,
    charAt: PropTypes.func.isRequired,
    charCodeAt: PropTypes.func.isRequired,
    codePointAt: PropTypes.func.isRequired,
    concat: PropTypes.func.isRequired,
    endsWith: PropTypes.func.isRequired,
    fixed: PropTypes.func.isRequired,
    fontcolor: PropTypes.func.isRequired,
    fontsize: PropTypes.func.isRequired,
    includes: PropTypes.func.isRequired,
    indexOf: PropTypes.func.isRequired,
    italics: PropTypes.func.isRequired,
    lastIndexOf: PropTypes.func.isRequired,
    length: PropTypes.number.isRequired,
    link: PropTypes.func.isRequired,
    localeCompare: PropTypes.func.isRequired,
    match: PropTypes.func.isRequired,
    matchAll: PropTypes.func.isRequired,
    normalize: PropTypes.func.isRequired,
    padEnd: PropTypes.func.isRequired,
    padStart: PropTypes.func.isRequired,
    repeat: PropTypes.func.isRequired,
    replace: PropTypes.func.isRequired,
    search: PropTypes.func.isRequired,
    slice: PropTypes.func.isRequired,
    small: PropTypes.func.isRequired,
    split: PropTypes.func.isRequired,
    startsWith: PropTypes.func.isRequired,
    strike: PropTypes.func.isRequired,
    sub: PropTypes.func.isRequired,
    substr: PropTypes.func.isRequired,
    substring: PropTypes.func.isRequired,
    sup: PropTypes.func.isRequired,
    toLocaleLowerCase: PropTypes.func.isRequired,
    toLocaleUpperCase: PropTypes.func.isRequired,
    toLowerCase: PropTypes.func.isRequired,
    toString: PropTypes.func.isRequired,
    toUpperCase: PropTypes.func.isRequired,
    trim: PropTypes.func.isRequired,
    trimEnd: PropTypes.func.isRequired,
    trimLeft: PropTypes.func.isRequired,
    trimRight: PropTypes.func.isRequired,
    trimStart: PropTypes.func.isRequired,
    valueOf: PropTypes.func.isRequired
  })]),
  /**
   * The value of the `input` element, required for a controlled component.
   */
  value: PropTypes.any,
  /**
   * Indicates whether the label should be wrapped.
   *
   * **Important:** It is recommended to avoid using this property unless absolutely necessary,
   * as wrapping labels is not the preferred approach due to potential layout issues.
   *
   * For more information and discussion, refer to the following issue on GitHub:
   * {@link https://github.com/mui/material-ui/issues/12255 MUI GitHub Issue #12255}
   */
  wrapLabel: PropTypes.bool
} : void 0;
export default TextField;